import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { isArray } from 'lodash';
import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import { UserType } from 'api/models';
import { humanise, humaniseWithDay } from 'components/atoms/humanised-date';
import { useUser } from 'hooks/use-user';
import { getTimeOffDurationPerDay } from 'utils/time-off-utils';
const GetTooltipHeader = ({ userName, timeOff })=>{
    const intl = useIntl();
    const timeOffDurationPerDay = getTimeOffDurationPerDay(timeOff === null || timeOff === void 0 ? void 0 : timeOff.timeOffDuration, timeOff === null || timeOff === void 0 ? void 0 : timeOff.timeOffType, intl);
    return /*#__PURE__*/ _jsx("div", {
        children: /*#__PURE__*/ _jsx(FormattedMessage, {
            id: "timeoff.tooltip.header_message_vendor",
            values: {
                userName,
                strong: (chunks)=>/*#__PURE__*/ _jsx("strong", {
                        children: chunks
                    }),
                timeOffDuration: timeOffDurationPerDay || ''
            }
        })
    });
};
const GetTooltipContent = ({ timeOff: { timeOffStart, timeOffEnd, note } })=>{
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            timeOffStart === timeOffEnd ? /*#__PURE__*/ _jsx("div", {
                children: /*#__PURE__*/ _jsx("strong", {
                    children: humaniseWithDay(moment(timeOffStart))
                })
            }) : /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("strong", {
                        children: humanise(moment(timeOffStart))
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: ' to '
                    }),
                    /*#__PURE__*/ _jsx("strong", {
                        children: humanise(moment(timeOffEnd))
                    })
                ]
            }),
            note && /*#__PURE__*/ _jsx("div", {
                children: /*#__PURE__*/ _jsx("i", {
                    children: `"${note}"`
                })
            })
        ]
    });
};
const SingleDayTimeoffContentForClient = ({ timeOff, userName })=>{
    const intl = useIntl();
    const timeOffDurationPerDay = getTimeOffDurationPerDay(timeOff === null || timeOff === void 0 ? void 0 : timeOff.timeOffDuration, timeOff === null || timeOff === void 0 ? void 0 : timeOff.timeOffType, intl);
    return /*#__PURE__*/ _jsx(FormattedMessage, {
        id: "timeoff.indicator.timeoff_warning_single_date",
        values: {
            strong: (chunks)=>/*#__PURE__*/ _jsx("strong", {
                    children: chunks
                }),
            user: userName || '',
            date: moment(timeOff === null || timeOff === void 0 ? void 0 : timeOff.timeOffStart).format('DD MMM YY') || '',
            timeOffDuration: timeOffDurationPerDay || ''
        }
    });
};
const RangeTimeoffContentForClient = ({ timeOff, userName })=>{
    const intl = useIntl();
    const timeOffDurationPerDay = getTimeOffDurationPerDay(timeOff === null || timeOff === void 0 ? void 0 : timeOff.timeOffDuration, timeOff === null || timeOff === void 0 ? void 0 : timeOff.timeOffType, intl);
    return /*#__PURE__*/ _jsx(FormattedMessage, {
        id: "timeoff.indicator.timeoff_warning_range",
        values: {
            strong: (chunks)=>/*#__PURE__*/ _jsx("strong", {
                    children: chunks
                }),
            user: userName || '',
            startDate: moment(timeOff.timeOffStart).format('DD MMM YY') || '',
            endDate: moment(timeOff.timeOffEnd).format('DD MMM YY') || '',
            timeOffDuration: timeOffDurationPerDay || ''
        }
    });
};
const MultipleDayTimeoffContentForClient = ({ userName, startDate, dueDate })=>{
    return /*#__PURE__*/ _jsx(FormattedMessage, {
        id: "timeoff.indicator.multiple_timeoff_warning_range",
        values: {
            strong: (chunks)=>/*#__PURE__*/ _jsx("strong", {
                    children: chunks
                }),
            user: userName || '',
            startDate: startDate.format('DD MMM YY'),
            endDate: dueDate.format('DD MMM YY')
        }
    });
};
export const GetTimeOffTooltipContent = ({ timeOff, userName, startDate, dueDate })=>{
    const { user } = useUser();
    if (!timeOff) {
        return null;
    }
    if (isArray(timeOff)) {
        if (user.userType === UserType.External) {
            if (startDate == null || dueDate == null) {
                return null;
            }
            if (timeOff.length === 1) {
                if (moment(timeOff[0].timeOffStart).isSame(timeOff[0].timeOffEnd, 'day')) {
                    return /*#__PURE__*/ _jsx(SingleDayTimeoffContentForClient, {
                        userName: userName,
                        timeOff: timeOff[0]
                    });
                }
                return /*#__PURE__*/ _jsx(RangeTimeoffContentForClient, {
                    userName: userName,
                    timeOff: timeOff[0]
                });
            }
            return /*#__PURE__*/ _jsx(MultipleDayTimeoffContentForClient, {
                userName: userName,
                startDate: startDate,
                dueDate: dueDate,
                timeOff: timeOff[0]
            });
        }
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(GetTooltipHeader, {
                    userName: userName,
                    timeOff: timeOff[0]
                }),
                timeOff.map((timeOffT, index)=>/*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(GetTooltipContent, {
                                timeOff: timeOffT
                            }),
                            index !== timeOff.length - 1 && /*#__PURE__*/ _jsx("br", {})
                        ]
                    }))
            ]
        });
    }
    if (user.userType === UserType.External) {
        if (moment(timeOff.timeOffStart).isSame(timeOff.timeOffEnd, 'day')) {
            return /*#__PURE__*/ _jsx(SingleDayTimeoffContentForClient, {
                userName: userName,
                timeOff: timeOff
            });
        }
        return /*#__PURE__*/ _jsx(RangeTimeoffContentForClient, {
            userName: userName,
            timeOff: timeOff
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(GetTooltipHeader, {
                userName: userName,
                timeOff: timeOff
            }),
            /*#__PURE__*/ _jsx(GetTooltipContent, {
                timeOff: timeOff
            })
        ]
    });
};
